'use client';

import type { ReactNode } from 'react';
import { FormField, SheetFormPage } from '../../components/SheetFormPage';
import { FIELD, FormLook } from './ui';

/** SheetFormPage in the finder family's look. Every form in a sheet goes through this. */
export function Form({
  description,
  helperText,
  submitLabel = 'Save',
  isValid = true,
  isSubmitting = false,
  onSubmit,
  children,
}: {
  description?: ReactNode;
  helperText?: ReactNode;
  submitLabel?: string;
  isValid?: boolean;
  isSubmitting?: boolean;
  onSubmit: () => void;
  children: ReactNode;
}) {
  return (
    <FormLook>
      <SheetFormPage
        description={description}
        helperText={helperText}
        submitLabel={submitLabel}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onSubmit={onSubmit}
      >
        {children}
      </SheetFormPage>
    </FormLook>
  );
}

export function TextField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required,
  help,
  autoComplete,
  inputMode,
  maxLength = 100,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'email' | 'tel' | 'date' | 'password' | 'number';
  placeholder?: string;
  required?: boolean;
  help?: ReactNode;
  autoComplete?: string;
  inputMode?: 'text' | 'tel' | 'email' | 'numeric' | 'decimal';
  maxLength?: number;
  autoFocus?: boolean;
}) {
  return (
    <FormField label={label} required={required} help={help}>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        maxLength={type === 'date' ? undefined : maxLength}
        autoFocus={autoFocus}
        className={FIELD}
      />
    </FormField>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder = 'Not set',
  required,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  options: { id: number; label: string }[];
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <FormField label={label} required={required}>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        className={FIELD}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </FormField>
  );
}

/** A select whose options sit under headings (Toddler / Youth / Adult for shirt sizes). */
export function GroupedSelectField({
  label,
  value,
  onChange,
  groups,
  placeholder = 'Not set',
  help,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  groups: { name: string; options: { id: number; label: string }[] }[];
  placeholder?: string;
  help?: ReactNode;
}) {
  return (
    <FormField label={label} help={help}>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        className={FIELD}
      >
        <option value="">{placeholder}</option>
        {groups
          .filter((g) => g.options.length > 0)
          .map((g) => (
            <optgroup key={g.name} label={g.name}>
              {g.options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </optgroup>
          ))}
      </select>
    </FormField>
  );
}

export function TextArea({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
  help,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  help?: ReactNode;
}) {
  return (
    <FormField label={label} help={help}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        maxLength={2000}
        className={FIELD}
      />
    </FormField>
  );
}
